import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Workflow } from "../types";
import { moduleLabel } from "../utils";
import { WorkflowStatusBadges } from "./WorkflowStatusBadges";

export function isReady(w: Workflow) {
  const hasSteps = (w.stepCount ?? w.steps?.length ?? 0) > 0;
  const routable = w.defaultWorkflow || (w.ruleCount ?? w.rules?.length ?? 0) > 0;
  return w.published && w.active && hasSteps && routable;
}

function readinessIssues(w: Workflow): string[] {
  const issues: string[] = [];
  if (!w.published) issues.push("Not published");
  if (!w.active) issues.push("Inactive");
  if ((w.stepCount ?? w.steps?.length ?? 0) === 0) issues.push("No steps");
  if (!w.defaultWorkflow && (w.ruleCount ?? w.rules?.length ?? 0) === 0)
    issues.push("No default or rules");
  return issues;
}

/** Compact row that expands on click to show details, with an Open button to navigate. */
export function WorkflowCard({ workflow }: { workflow: Workflow }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const ready = isReady(workflow);
  const issues = readinessIssues(workflow);
  const stepCount = workflow.stepCount ?? workflow.steps?.length ?? 0;
  const ruleCount = workflow.ruleCount ?? workflow.rules?.length ?? 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Collapsed row — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        {/* Ready indicator dot */}
        <span
          className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${ready ? "bg-emerald-500" : "bg-amber-400"}`}
          title={ready ? "Ready" : issues.join(", ")}
        />

        {/* Name + code */}
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900">{workflow.name}</p>
          <p className="text-xs text-slate-400">{workflow.code}</p>
        </div>

        {/* Module chip */}
        <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
          {moduleLabel(workflow.moduleCode)}
        </span>

        {/* Step/rule counts */}
        <span className="shrink-0 text-xs text-slate-400">
          {stepCount}s / {ruleCount}r
        </span>

        {/* Expand chevron */}
        <span className="shrink-0 text-slate-400 text-sm">
          {expanded ? "▲" : "▼"}
        </span>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3">
          {workflow.description && (
            <p className="mb-3 text-sm text-slate-500">{workflow.description}</p>
          )}

          <WorkflowStatusBadges workflow={workflow} />

          {/* Readiness */}
          <div className={`mt-3 rounded-lg px-3 py-2 text-xs font-medium ${
            ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
          }`}>
            {ready ? "✓ Ready — will be used for approvals" : `⚠ Not ready — ${issues.join(", ")}`}
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span>{stepCount} step{stepCount !== 1 ? "s" : ""} · {ruleCount} rule{ruleCount !== 1 ? "s" : ""}</span>
            <button
              type="button"
              onClick={() => navigate(`/admin/workflows/${workflow.id}`)}
              className="rounded-lg bg-slate-900 px-3 py-1.5 font-semibold text-white hover:bg-slate-700"
            >
              Open →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}